import React, { useState, useMemo } from 'react';
import {
  Search,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Sliders,
  Users,
  TrendingUp,
  DollarSign,
  Database,
  ShieldCheck,
  ChevronRight,
  Plus,
  RefreshCw,
  Clock,
  Filter,
  X,
  Globe,
  Key,
  CreditCard,
  History,
  Zap,
  Download,
  Mail,
  RotateCcw,
  Shield,
  Settings,
  Edit3,
  Eye,
  EyeOff,
  Info,
  Server,
  Terminal,
  Cpu,
  Layers,
  FileText,
  Lock,
  Power,
  Check,
  Copy,
  ExternalLink,
  Code2,
  ToggleLeft,
  ToggleRight,
  Save,
  Trash2,
  Radio,
  SlidersHorizontal,
  Bell,
  Calendar,
  Sparkles,
  HelpCircle
} from 'lucide-react';
import { AppView } from '../../types';

interface SystemConfigurationManagementProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

export type ConfigSubTab =
  | 'General'
  | 'Markets & Locales'
  | 'Providers'
  | 'Integrations'
  | 'Feature Flags'
  | 'Roles & Permissions'
  | 'Environment'
  | 'Developer / API Configuration';

export interface ProviderItem {
  id: string;
  name: string;
  serviceType: string;
  status: 'Not configured' | 'Testing' | 'Active' | 'Degraded' | 'Disabled';
  capability: string;
  configurationState: 'Unset' | 'Draft' | 'Configured' | 'Verified';
  runtimeStatus: 'Operational' | 'Testing' | 'Unreachable' | 'Idle';
  lastCheck: string;
  latencyMs?: number;
  environment: 'Production' | 'Sandbox';
  apiKeyMasked?: string;
  webhookEndpoint?: string;
  notes?: string;
}

export interface IntegrationItem {
  id: string;
  name: string;
  category: 'Accounting' | 'CRM' | 'Analytics' | 'Security / SIEM' | 'Communication';
  status: 'Connected' | 'Not configured' | 'Action Required';
  syncInterval: string;
  lastSync: string;
  apiVersion: string;
  description: string;
}

export interface FeatureFlagItem {
  id: string;
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  targetAudience: 'All Users' | 'Internal Staff' | 'Beta Cohort' | 'Regional Tier 1';
  lastModified: string;
  modifiedBy: string;
  impactLevel: 'Low' | 'Medium' | 'Critical';
}

export interface MarketLocaleItem {
  id: string;
  code: string;
  country: string;
  primaryCurrency: string;
  status: 'Active' | 'Beta' | 'Disabled';
  defaultLanguage: string;
  supportedLanguages: string[];
  settlementCutoff: string;
  regulatoryJurisdiction: string;
}

export interface EnvironmentVariableItem {
  key: string;
  valueMasked: string;
  type: 'Secret' | 'Config' | 'Endpoint';
  environment: 'Production' | 'Staging' | 'Development';
  lastRotated: string;
  rotatedBy: string;
  description: string;
}

export const SystemConfigurationManagement: React.FC<SystemConfigurationManagementProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  // Navigation & Sub-tabs
  const [selectedDomain, setSelectedDomain] = useState<string>('configuration');
  const [activeSubTab, setActiveSubTab] = useState<ConfigSubTab>('General');

  // Search Filter
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // ==========================================
  // 1. GENERAL SETTINGS STATE
  // ==========================================
  const initialGeneralState = {
    platformName: 'YamooPay',
    supportEmail: 'support@yamoopay.com',
    maintenanceMode: false,
    publicApiRateLimit: '10,000 req/min',
    enforceMfaForStaff: true,
    sessionTimeoutMins: 30,
    systemBannerNotice: ''
  };

  const [generalSettings, setGeneralSettings] = useState(initialGeneralState);
  const [savedGeneralSettings, setSavedGeneralSettings] = useState(initialGeneralState);

  // Maintenance Mode High-Risk Modal State
  const [showMaintenanceConfirmModal, setShowMaintenanceConfirmModal] = useState<boolean>(false);
  const [pendingMaintenanceState, setPendingMaintenanceState] = useState<boolean>(false);
  const [maintenanceReason, setMaintenanceReason] = useState<string>('');
  const [maintenanceImpactAccepted, setMaintenanceImpactAccepted] = useState<boolean>(false);
  const [maintenanceEstimatedDuration, setMaintenanceEstimatedDuration] = useState<string>('30 minutes');

  // ==========================================
  // 2. PROVIDERS DYNAMIC STATE (Dynamic schema, NOT hardcoded single-source)
  // ==========================================
  const [providers, setProviders] = useState<ProviderItem[]>([
    {
      id: 'prov-001',
      name: 'Stripe',
      serviceType: 'Payment Gateway',
      status: 'Not configured',
      capability: 'Card Clearing & 3DS2 Processing',
      configurationState: 'Unset',
      runtimeStatus: 'Idle',
      lastCheck: '2026-08-28 04:15 UTC',
      environment: 'Sandbox',
      apiKeyMasked: 'sk_test_••••••••••••••••••••94fA',
      webhookEndpoint: 'https://api.yamoopay.com/v1/webhooks/gateway-alpha',
      notes: 'Global credit & debit acquisition rail.'
    },
    {
      id: 'prov-002',
      name: 'Plaid',
      serviceType: 'Bank Authentication',
      status: 'Testing',
      capability: 'Open Banking Account Verification',
      configurationState: 'Configured',
      runtimeStatus: 'Testing',
      lastCheck: '2026-08-28 04:22 UTC',
      latencyMs: 142,
      environment: 'Sandbox',
      apiKeyMasked: 'sandbox_••••••••••••••••••••10c8',
      webhookEndpoint: 'https://api.yamoopay.com/v1/webhooks/open-banking-sandbox',
      notes: 'Direct debit bank account linking and ACH mandate verification.'
    },
    {
      id: 'prov-003',
      name: 'Twilio',
      serviceType: 'SMS / 2FA',
      status: 'Not configured',
      capability: 'Telephony & Push OTP Delivery',
      configurationState: 'Unset',
      runtimeStatus: 'Idle',
      lastCheck: '2026-08-28 03:50 UTC',
      environment: 'Sandbox',
      apiKeyMasked: 'AC••••••••••••••••••••••••••••••',
      webhookEndpoint: 'https://api.yamoopay.com/v1/webhooks/telephony-status',
      notes: 'Multi-factor authentication SMS gateway & fallback voice OTP.'
    },
    {
      id: 'prov-004',
      name: 'Socure',
      serviceType: 'Identity & KYC Engine',
      status: 'Active',
      capability: 'Predictive Document Verification',
      configurationState: 'Verified',
      runtimeStatus: 'Operational',
      lastCheck: '2026-08-28 04:39 UTC',
      latencyMs: 230,
      environment: 'Production',
      apiKeyMasked: 'prod_soc_••••••••••••••••••••33e1',
      webhookEndpoint: 'https://api.yamoopay.com/v1/webhooks/kyc-socure',
      notes: 'Automated biometric identity & synthetic fraud validation.'
    },
    {
      id: 'prov-005',
      name: 'Chainalysis',
      serviceType: 'AML & Blockchain Sanctions',
      status: 'Active',
      capability: 'Real-Time Wallet Risk Scoring',
      configurationState: 'Verified',
      runtimeStatus: 'Operational',
      lastCheck: '2026-08-28 04:35 UTC',
      latencyMs: 185,
      environment: 'Production',
      apiKeyMasked: 'kyc_chain_••••••••••••••••••••88d9',
      webhookEndpoint: 'https://api.yamoopay.com/v1/webhooks/aml-kyt',
      notes: 'KYT transaction screening & FATF Travel Rule protocol integration.'
    }
  ]);

  // Provider Modal State
  const [selectedProviderToConfigure, setSelectedProviderToConfigure] = useState<ProviderItem | null>(null);
  const [providerFormStatus, setProviderFormStatus] = useState<'Not configured' | 'Testing' | 'Active' | 'Disabled'>('Not configured');
  const [providerFormEnv, setProviderFormEnv] = useState<'Production' | 'Sandbox'>('Sandbox');
  const [providerFormApiKey, setProviderFormApiKey] = useState<string>('');
  const [providerFormWebhook, setProviderFormWebhook] = useState<string>('');
  const [showSecretKey, setShowSecretKey] = useState<boolean>(false);
  const [showNewProviderModal, setShowNewProviderModal] = useState<boolean>(false);
  const [newCustomProviderName, setNewCustomProviderName] = useState<string>('');
  const [newCustomProviderService, setNewCustomProviderService] = useState<string>('');
  const [newCustomProviderCapability, setNewCustomProviderCapability] = useState<string>('');

  // ==========================================
  // 3. MARKETS & LOCALES STATE
  // ==========================================
  const [markets, setMarkets] = useState<MarketLocaleItem[]>([
    {
      id: 'mkt-fr',
      code: 'FR',
      country: 'France',
      primaryCurrency: 'EUR (€)',
      status: 'Active',
      defaultLanguage: 'fr-FR',
      supportedLanguages: ['fr-FR', 'en-US'],
      settlementCutoff: '16:30 CET (TARGET2)',
      regulatoryJurisdiction: 'ACPR / Banque de France'
    },
    {
      id: 'mkt-us',
      code: 'US',
      country: 'United States',
      primaryCurrency: 'USD ($)',
      status: 'Active',
      defaultLanguage: 'en-US',
      supportedLanguages: ['en-US', 'es-US'],
      settlementCutoff: '17:00 EST (Fedwire)',
      regulatoryJurisdiction: 'FinCEN / OCC'
    },
    {
      id: 'mkt-gb',
      code: 'GB',
      country: 'United Kingdom',
      primaryCurrency: 'GBP (£)',
      status: 'Active',
      defaultLanguage: 'en-GB',
      supportedLanguages: ['en-GB'],
      settlementCutoff: '16:00 GMT (CHAPS)',
      regulatoryJurisdiction: 'FCA (EMI Authorised)'
    },
    {
      id: 'mkt-de',
      code: 'DE',
      country: 'Germany',
      primaryCurrency: 'EUR (€)',
      status: 'Active',
      defaultLanguage: 'de-DE',
      supportedLanguages: ['de-DE', 'en-US'],
      settlementCutoff: '16:30 CET (SEPA Core)',
      regulatoryJurisdiction: 'BaFin'
    },
    {
      id: 'mkt-sg',
      code: 'SG',
      country: 'Singapore',
      primaryCurrency: 'SGD (S$)',
      status: 'Beta',
      defaultLanguage: 'en-SG',
      supportedLanguages: ['en-SG', 'zh-SG'],
      settlementCutoff: '18:00 SGT (MEPS+)',
      regulatoryJurisdiction: 'MAS (Major Payment Institution)'
    }
  ]);

  // ==========================================
  // 4. INTEGRATIONS STATE
  // ==========================================
  const [integrations, setIntegrations] = useState<IntegrationItem[]>([
    {
      id: 'int-001',
      name: 'NetSuite ERP Direct Connector',
      category: 'Accounting',
      status: 'Connected',
      syncInterval: 'Every 15 mins',
      lastSync: '2026-08-28 04:30 UTC',
      apiVersion: 'v2024.1 (RESTlets)',
      description: 'Automated 2-way ledger journaling of settled receivables and treasury sweeps.'
    },
    {
      id: 'int-002',
      name: 'Salesforce Financial Services Cloud',
      category: 'CRM',
      status: 'Connected',
      syncInterval: 'Hourly',
      lastSync: '2026-08-28 04:00 UTC',
      apiVersion: 'v59.0 REST',
      description: 'Account tier sync, merchant tier status, and compliance case linkage.'
    },
    {
      id: 'int-003',
      name: 'Datadog SIEM & Telemetry',
      category: 'Security / SIEM',
      status: 'Connected',
      syncInterval: 'Real-time Streaming',
      lastSync: 'Live',
      apiVersion: 'v2 Logs Ingestion',
      description: 'Zero-delay export of infrastructure audit logs and WAF anomaly telemetry.'
    },
    {
      id: 'int-004',
      name: 'Slack Security Webhooks',
      category: 'Communication',
      status: 'Connected',
      syncInterval: 'Event-driven',
      lastSync: '2026-08-28 04:12 UTC',
      apiVersion: 'v1 Incoming Webhook',
      description: 'Automated dispatch of Tier-1 critical alarms to #ops-incident-bridge.'
    },
    {
      id: 'int-005',
      name: 'Snowflake Enterprise Lakehouse',
      category: 'Analytics',
      status: 'Not configured',
      syncInterval: 'Nightly Batch',
      lastSync: 'Never',
      apiVersion: 'v1 SQL Pipeline',
      description: 'Consolidated read-only data replica for quantitative corporate analytics.'
    }
  ]);

  // ==========================================
  // 5. FEATURE FLAGS STATE
  // ==========================================
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagItem[]>([
    {
      id: 'flag-001',
      key: 'ff_instant_sepa_direct',
      name: 'Instant SEPA Credit Transfer (SCT Inst)',
      description: 'Enable real-time 10-second settlement with participating European clearing banks.',
      enabled: true,
      targetAudience: 'All Users',
      lastModified: '2026-08-20',
      modifiedBy: 'CPO Lead',
      impactLevel: 'Critical'
    },
    {
      id: 'flag-002',
      key: 'ff_ai_autonomous_reconciliation',
      name: 'AI-Assisted Automated Recon (Sovereign Engine)',
      description: 'Auto-reconcile unmatched bank items exceeding 99.4% confidence score.',
      enabled: true,
      targetAudience: 'Internal Staff',
      lastModified: '2026-08-25',
      modifiedBy: 'CAIO Lead',
      impactLevel: 'Medium'
    },
    {
      id: 'flag-003',
      key: 'ff_biometric_fido2_passkeys',
      name: 'FIDO2 / WebAuthn Biometric Passkeys',
      description: 'Allow merchants to register hardware security keys for frictionless 2FA.',
      enabled: true,
      targetAudience: 'All Users',
      lastModified: '2026-08-15',
      modifiedBy: 'CISO Office',
      impactLevel: 'Low'
    },
    {
      id: 'flag-004',
      key: 'ff_multi_currency_virtual_iban',
      name: 'Multi-Currency Virtual IBAN Allocator',
      description: 'Instant multi-jurisdiction IBAN generation for cross-border business accounts.',
      enabled: false,
      targetAudience: 'Beta Cohort',
      lastModified: '2026-08-27',
      modifiedBy: 'Product Bet Matrix',
      impactLevel: 'Critical'
    },
    {
      id: 'flag-005',
      key: 'ff_crypto_offramp_stablecoin',
      name: 'USDC / EURC Treasury Liquidity Rails',
      description: 'Automatic liquidity conversion and treasury balancing via regulated stablecoin reserves.',
      enabled: false,
      targetAudience: 'Beta Cohort',
      lastModified: '2026-08-22',
      modifiedBy: 'Corporate Finance Lead',
      impactLevel: 'Critical'
    }
  ]);

  // ==========================================
  // 6. ENVIRONMENT VARIABLES & SECRETS (Never displayed in clear text!)
  // ==========================================
  const [environmentSecrets, setEnvironmentSecrets] = useState<EnvironmentVariableItem[]>([
    {
      key: 'DATABASE_PRIMARY_CLUSTER_URI',
      valueMasked: 'postgres://yamoopay_admin:••••••••••••••••••••@pg-prod-primary.eu-west-3.internal:5432/ledger_v2',
      type: 'Secret',
      environment: 'Production',
      lastRotated: '2026-08-01',
      rotatedBy: 'DevOps Lead',
      description: 'Primary ACID transactional ledger connection string with TLS v1.3 strict enforcement.'
    },
    {
      key: 'KMS_MASTER_KEY_ARN',
      valueMasked: 'arn:aws:kms:eu-west-3:98879950313:key/••••••••-••••-••••-••••-••••••••••••',
      type: 'Secret',
      environment: 'Production',
      lastRotated: '2026-07-15',
      rotatedBy: 'CISO Office',
      description: 'Hardware Security Module envelope encryption key for sensitive PAN and IBAN storage.'
    },
    {
      key: 'REDIS_CACHE_CLUSTER_AUTH',
      valueMasked: 'redis://:••••••••••••••••••••••••••••••••@cache-cluster.internal:6379',
      type: 'Secret',
      environment: 'Production',
      lastRotated: '2026-08-10',
      rotatedBy: 'Platform Engineer',
      description: 'In-memory rate limiting and idempotency token verification store.'
    },
    {
      key: 'API_GATEWAY_INGRESS_DOMAIN',
      valueMasked: 'https://api.yamoopay.com/v1',
      type: 'Endpoint',
      environment: 'Production',
      lastRotated: '2026-01-01',
      rotatedBy: 'System',
      description: 'Public TLS gateway ingress for merchant APIs.'
    },
    {
      key: 'JWT_SIGNING_PRIVATE_KEY_SECP256K1',
      valueMasked: '-----BEGIN EC PRIVATE KEY-----\n••••••••••••••••••••••••••••••••\n-----END EC PRIVATE KEY-----',
      type: 'Secret',
      environment: 'Production',
      lastRotated: '2026-08-15',
      rotatedBy: 'Security Bot',
      description: 'Elliptic curve key used to sign zero-trust JWT bearer tokens for staff & API clients.'
    }
  ]);

  // Secret Rotation Modal
  const [selectedSecretToRotate, setSelectedSecretToRotate] = useState<EnvironmentVariableItem | null>(null);
  const [newSecretValueInput, setNewSecretValueInput] = useState<string>('');
  const [secretRotationReason, setSecretRotationReason] = useState<string>('');

  // Unsaved Changes Tracking
  const hasUnsavedChanges = useMemo(() => {
    return (
      generalSettings.platformName !== savedGeneralSettings.platformName ||
      generalSettings.supportEmail !== savedGeneralSettings.supportEmail ||
      generalSettings.maintenanceMode !== savedGeneralSettings.maintenanceMode ||
      generalSettings.publicApiRateLimit !== savedGeneralSettings.publicApiRateLimit ||
      generalSettings.enforceMfaForStaff !== savedGeneralSettings.enforceMfaForStaff ||
      generalSettings.sessionTimeoutMins !== savedGeneralSettings.sessionTimeoutMins
    );
  }, [generalSettings, savedGeneralSettings]);

  // Trigger Toast
  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3800);
  };

  // Discard changes
  const handleDiscardChanges = () => {
    setGeneralSettings({ ...savedGeneralSettings });
    triggerToast('All unsaved changes were discarded.');
  };

  // Save configuration with Dual Review / Confirmation if high risk
  const [showReviewChangesModal, setShowReviewChangesModal] = useState<boolean>(false);

  const handleSaveConfigurationSubmit = () => {
    setSavedGeneralSettings({ ...generalSettings });
    setShowReviewChangesModal(false);
    triggerToast('Configuration saved successfully. Audit trail record logged #CONF-8910.');
  };

  // Toggle Maintenance Mode Request
  const handleToggleMaintenanceMode = (checked: boolean) => {
    setPendingMaintenanceState(checked);
    setMaintenanceReason('');
    setMaintenanceImpactAccepted(false);
    setShowMaintenanceConfirmModal(true);
  };

  const handleConfirmMaintenanceMode = () => {
    setGeneralSettings({ ...generalSettings, maintenanceMode: pendingMaintenanceState });
    setShowMaintenanceConfirmModal(false);
    triggerToast(
      pendingMaintenanceState
        ? '⚠️ MAINTENANCE MODE ACTIVATED: Public traffic is redirected to static 503 maintenance page. Admin session remains active.'
        : '✅ MAINTENANCE MODE DEACTIVATED: Public API and merchant portal traffic restored to normal.'
    );
  };

  // Filtered Provider items
  const filteredProviders = useMemo(() => {
    if (!searchQuery.trim()) return providers;
    const q = searchQuery.toLowerCase();
    return providers.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.serviceType.toLowerCase().includes(q) ||
        p.status.toLowerCase().includes(q) ||
        p.capability.toLowerCase().includes(q)
    );
  }, [providers, searchQuery]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (Canonical 7 Domains with Active Configuration) */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F4F4F] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <span className="font-black text-white text-base leading-none">Y</span>
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Administrator
            </span>
          </div>
        </div>

        {/* 7 Domains Navigation List */}
        <div className="flex-1 py-4 px-3 space-y-1">
          <button
            onClick={() => {
              setSelectedDomain('overview');
              onNavigateToView('admin-super-command-center');
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Layers className="w-4 h-4" />
            <span>Overview</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('customers');
              onNavigateToView('admin-organizations');
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Users className="w-4 h-4" />
            <span>Customers & Support</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('product');
              onNavigateToView('cpo-command-center');
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Product & Growth</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('revenue');
              onNavigateToView('admin-monetization');
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <DollarSign className="w-4 h-4" />
            <span>Revenue & Finance</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('data');
              onNavigateToView('admin-ai-platform');
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Database className="w-4 h-4" />
            <span>Data & AI</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('operations');
              onNavigateToView('admin-compliance');
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <ShieldCheck className="w-4 h-4" />
            <span>Operations & Risk</span>
          </button>

          {/* ACTIVE: Configuration (Dark Forest Green Pill from Reference Screenshot) */}
          <button
            onClick={() => setSelectedDomain('configuration')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold bg-[#0F4F4F] text-white shadow-xs transition-all cursor-pointer"
          >
            <Settings className="w-4 h-4 text-white" />
            <span>Configuration</span>
          </button>
        </div>

        {/* Sidebar Footer Profile (Matching Screenshot: YA YamooPay Admin v2.4.1 Stable) */}
        <div className="p-3 border-t border-[#F1F5F9]">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F4F4F] text-white font-bold text-xs flex items-center justify-center">
              YA
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">YamooPay Admin</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">v2.4.1 (Stable)</span>
            </div>
          </div>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN WORKSPACE AREA                                         */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar with Search & Utility Icons (Screenshot match) */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-20 gap-6">
          <div className="flex items-center gap-6 flex-1 max-w-2xl">
            <h2 className="text-lg font-bold text-[#0F172A] whitespace-nowrap">
              Configuration
            </h2>
            <div className="relative w-full max-w-md">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search settings..."
                className="w-full pl-9 pr-4 py-1.5 bg-[#F8FAFC] hover:bg-white border border-[#E2E8F0] rounded-xl text-xs text-[#0F172A] placeholder-[#94A3B8] outline-none focus:border-[#0F4F4F] focus:ring-1 focus:ring-[#0F4F4F] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => triggerToast('Security policy: Multi-region configuration sync active.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Security Status"
            >
              <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
            </button>
            <button
              onClick={() => triggerToast('Audit trail: All changes signed with HMAC-SHA256.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Audit Logs"
            >
              <FileText className="w-4 h-4" />
            </button>
            <button
              onClick={() => triggerToast('Notifications: 0 pending configuration alerts.')}
              className="relative text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Alerts"
            >
              <Bell className="w-4 h-4" />
              <span className="w-2 h-2 bg-red-500 rounded-full absolute top-1.5 right-1.5 ring-2 ring-white"></span>
            </button>
            <div className="w-8 h-8 rounded-full bg-[#E2E8F0] flex items-center justify-center text-[#475569] text-xs font-bold">
              YA
            </div>
          </div>
        </header>

        {/* Top Action Bar (Exact match to screenshot: Description + Discard Changes + Save Configuration) */}
        <div className="px-8 py-4 bg-white border-b border-[#E2E8F0] flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs text-[#64748B]">
              Manage system-wide operational parameters, integrations, and environment flags.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleDiscardChanges}
              disabled={!hasUnsavedChanges}
              className={`px-4 py-2 bg-white rounded-xl text-xs font-semibold border border-[#CBD5E1] transition-all cursor-pointer ${
                hasUnsavedChanges
                  ? 'text-[#0F172A] hover:bg-[#F8FAFC] shadow-2xs'
                  : 'text-[#94A3B8] opacity-60 cursor-not-allowed'
              }`}
            >
              Discard Changes
            </button>

            <button
              onClick={() => setShowReviewChangesModal(true)}
              className={`px-5 py-2 rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer flex items-center gap-2 ${
                hasUnsavedChanges
                  ? 'bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white'
                  : 'bg-[#0F4F4F] text-white hover:bg-[#0A3D3D]'
              }`}
            >
              <Save className="w-3.5 h-3.5" />
              <span>Save Configuration</span>
              {hasUnsavedChanges && (
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              )}
            </button>
          </div>
        </div>

        {/* MAIN BODY GRID: Secondary Sidebar + Content Panel */}
        <div className="flex-1 flex min-h-0">
          {/* =============================================================== */}
          {/* SECONDARY NAVIGATION SIDEBAR (Exact prompt specification)       */}
          {/* =============================================================== */}
          <aside className="w-60 bg-white border-r border-[#E2E8F0] p-4 shrink-0 space-y-1.5">
            {([
              'General',
              'Markets & Locales',
              'Providers',
              'Integrations',
              'Feature Flags',
              'Roles & Permissions',
              'Environment',
              'Developer / API Configuration'
            ] as ConfigSubTab[]).map((tab) => {
              const isActive = activeSubTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => {
                    if (tab === 'Roles & Permissions') {
                      onNavigateToView('admin-roles-permissions');
                    } else if (tab === 'Developer / API Configuration') {
                      onNavigateToView('developer-command-center');
                    } else {
                      setActiveSubTab(tab);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#D1F2E7] text-[#0A4D3C] font-bold'
                      : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
                  }`}
                >
                  <span>{tab}</span>
                  {isActive && <ChevronRight className="w-4 h-4 text-[#0A4D3C]" />}
                </button>
              );
            })}

            {/* Regulatory Domain Notice */}
            <div className="pt-6 mt-6 border-t border-[#F1F5F9] px-2 text-[11px] text-[#64748B] space-y-2">
              <div className="flex items-center gap-1.5 text-[#0F4F4F] font-bold">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Financial Boundary</span>
              </div>
              <p className="leading-relaxed">
                TVA, statutory tax brackets, and transaction limits are managed under dedicated Financial Rulesets & Corporate Finance.
              </p>
            </div>
          </aside>

          {/* =============================================================== */}
          {/* CONTENT PANEL ACCORDING TO ACTIVE SUBTAB                        */}
          {/* =============================================================== */}
          <main className="flex-1 p-8 overflow-y-auto space-y-8 max-w-5xl">
            {/* Toast feedback */}
            {toastMessage && (
              <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>{toastMessage}</span>
              </div>
            )}

            {/* ============================================================= */}
            {/* SUB-VIEW 1: GENERAL SETTINGS (Exact Screenshot Reproduction) */}
            {/* ============================================================= */}
            {activeSubTab === 'General' && (
              <div className="space-y-8">
                {/* 1. General Settings Section */}
                <div className="space-y-4">
                  <h3 className="text-base font-bold text-[#0F172A]">
                    General Settings
                  </h3>

                  <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-6">
                    {/* Inputs Row: Platform Name + Support Email (Screenshot Match) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#0F172A] block">
                          Platform Name
                        </label>
                        <input
                          type="text"
                          value={generalSettings.platformName}
                          onChange={(e) =>
                            setGeneralSettings({ ...generalSettings, platformName: e.target.value })
                          }
                          className="w-full px-3.5 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] focus:ring-1 focus:ring-[#0F4F4F] outline-none transition-all"
                        />
                        <p className="text-[11px] text-[#64748B]">
                          The primary display name used across all user-facing interfaces.
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-[#0F172A] block">
                          Support Email
                        </label>
                        <input
                          type="email"
                          value={generalSettings.supportEmail}
                          onChange={(e) =>
                            setGeneralSettings({ ...generalSettings, supportEmail: e.target.value })
                          }
                          className="w-full px-3.5 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] focus:ring-1 focus:ring-[#0F4F4F] outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div className="border-t border-[#F1F5F9]"></div>

                    {/* Maintenance Mode Row (Screenshot match: High-Risk Toggle) */}
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-bold text-[#0F172A]">
                            Maintenance Mode
                          </h4>
                          {generalSettings.maintenanceMode && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 animate-pulse">
                              Active in Production
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-[#64748B] mt-0.5">
                          Temporarily disable public access. Admin access remains active.
                        </p>
                      </div>

                      {/* Toggle Switch */}
                      <button
                        type="button"
                        onClick={() => handleToggleMaintenanceMode(!generalSettings.maintenanceMode)}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          generalSettings.maintenanceMode ? 'bg-red-600' : 'bg-[#CBD5E1]'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                            generalSettings.maintenanceMode ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* 2. Active Providers Section (Screenshot Match) */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#0F172A]">
                      Active Providers
                    </h3>
                    <button
                      onClick={() => setShowNewProviderModal(true)}
                      className="text-xs font-semibold text-[#0F4F4F] hover:text-[#0A3D3D] flex items-center gap-1 cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Register Custom Provider</span>
                    </button>
                  </div>

                  <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[11px] uppercase tracking-wider">
                          <th className="py-3.5 px-6">Provider</th>
                          <th className="py-3.5 px-6">Service Type</th>
                          <th className="py-3.5 px-6">Status</th>
                          <th className="py-3.5 px-6 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F1F5F9]">
                        {filteredProviders.map((provider) => (
                          <tr key={provider.id} className="hover:bg-[#F8FAFC] transition-colors">
                            {/* Provider */}
                            <td className="py-4 px-6 font-bold text-[#0F172A] whitespace-nowrap">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-[#F1F5F9] border border-[#E2E8F0] flex items-center justify-center text-[#0F4F4F] font-black text-xs">
                                  {provider.name.charAt(0)}
                                </div>
                                <span>{provider.name}</span>
                              </div>
                            </td>

                            {/* Service Type */}
                            <td className="py-4 px-6 text-[#475569] whitespace-nowrap">
                              {provider.serviceType}
                            </td>

                            {/* Status Pill (Exact Screenshot Match: Not configured / Testing / Active) */}
                            <td className="py-4 px-6 whitespace-nowrap">
                              {provider.status === 'Not configured' && (
                                <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-medium bg-[#F1F5F9] text-[#475569]">
                                  Not configured
                                </span>
                              )}
                              {provider.status === 'Testing' && (
                                <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-semibold bg-[#EFF6FF] text-[#2563EB]">
                                  Testing
                                </span>
                              )}
                              {provider.status === 'Active' && (
                                <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-semibold bg-[#DCFCE7] text-[#15803D]">
                                  Active
                                </span>
                              )}
                            </td>

                            {/* Actions (Screenshot match: "Configure") */}
                            <td className="py-4 px-6 text-right whitespace-nowrap">
                              <button
                                onClick={() => {
                                  setSelectedProviderToConfigure(provider);
                                  setProviderFormStatus(provider.status as any);
                                  setProviderFormEnv(provider.environment);
                                  setProviderFormApiKey(provider.apiKeyMasked || '');
                                  setProviderFormWebhook(provider.webhookEndpoint || '');
                                }}
                                className="text-xs font-semibold text-[#0F172A] hover:text-[#0F4F4F] cursor-pointer"
                              >
                                Configure
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* SUB-VIEW 2: PROVIDERS FULL RUNTIME & AUDIT TABLE             */}
            {/* ============================================================= */}
            {activeSubTab === 'Providers' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-base font-bold text-[#0F172A]">
                      External Infrastructure Providers
                    </h3>
                    <p className="text-xs text-[#64748B] mt-0.5">
                      Dynamic provider registry with live health ping, runtime state, and RBAC credential management.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNewProviderModal(true)}
                    className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl text-xs font-bold shadow-xs transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Register New Provider</span>
                  </button>
                </div>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[11px]">
                        <th className="py-3.5 px-6">Provider</th>
                        <th className="py-3.5 px-6">Capability</th>
                        <th className="py-3.5 px-6">Configuration State</th>
                        <th className="py-3.5 px-6">Runtime Status</th>
                        <th className="py-3.5 px-6">Last Check</th>
                        <th className="py-3.5 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F1F5F9]">
                      {providers.map((p) => (
                        <tr key={p.id} className="hover:bg-[#F8FAFC] transition-colors">
                          <td className="py-4 px-6 font-bold text-[#0F172A]">
                            <div className="flex items-center gap-2">
                              <span className="font-bold">{p.name}</span>
                              <span className="text-[10px] text-[#64748B] font-normal">({p.serviceType})</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-[#475569]">{p.capability}</td>
                          <td className="py-4 px-6">
                            <span
                              className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                                p.configurationState === 'Verified'
                                  ? 'bg-[#DCFCE7] text-[#15803D]'
                                  : p.configurationState === 'Configured'
                                  ? 'bg-[#EFF6FF] text-[#2563EB]'
                                  : 'bg-[#F1F5F9] text-[#475569]'
                              }`}
                            >
                              {p.configurationState}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-1.5">
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  p.runtimeStatus === 'Operational'
                                    ? 'bg-emerald-500'
                                    : p.runtimeStatus === 'Testing'
                                    ? 'bg-blue-500'
                                    : 'bg-gray-400'
                                }`}
                              ></span>
                              <span className="font-medium text-[#0F172A]">{p.runtimeStatus}</span>
                              {p.latencyMs && (
                                <span className="font-mono text-[10px] text-[#64748B]">({p.latencyMs}ms)</span>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-6 font-mono text-[11px] text-[#64748B]">{p.lastCheck}</td>
                          <td className="py-4 px-6 text-right">
                            <button
                              onClick={() => {
                                setSelectedProviderToConfigure(p);
                                setProviderFormStatus(p.status as any);
                                setProviderFormEnv(p.environment);
                                setProviderFormApiKey(p.apiKeyMasked || '');
                                setProviderFormWebhook(p.webhookEndpoint || '');
                              }}
                              className="px-3 py-1 bg-white border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#0F172A] font-bold rounded-lg text-xs cursor-pointer shadow-2xs"
                            >
                              Configure
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* SUB-VIEW 3: MARKETS & LOCALES                                */}
            {/* ============================================================= */}
            {activeSubTab === 'Markets & Locales' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-[#0F172A]">
                      Active Jurisdictions & Locales
                    </h3>
                    <p className="text-xs text-[#64748B] mt-0.5">
                      Configure sovereign operational zones, settlement cutoffs, and regulatory frameworks.
                    </p>
                  </div>
                  <button
                    onClick={() => onNavigateToView('global-expansion-command-center')}
                    className="px-4 py-2 bg-white border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#0F172A] font-bold rounded-xl text-xs flex items-center gap-1.5 cursor-pointer shadow-2xs"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    <span>Global Expansion Studio</span>
                  </button>
                </div>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[11px]">
                        <th className="py-3.5 px-6">Market</th>
                        <th className="py-3.5 px-6">Primary Currency</th>
                        <th className="py-3.5 px-6">Locales</th>
                        <th className="py-3.5 px-6">Clearing Cutoff</th>
                        <th className="py-3.5 px-6">Jurisdiction</th>
                        <th className="py-3.5 px-6 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F1F5F9]">
                      {markets.map((m) => (
                        <tr key={m.id} className="hover:bg-[#F8FAFC] transition-colors">
                          <td className="py-4 px-6 font-bold text-[#0F172A]">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-4 bg-[#F1F5F9] border border-[#CBD5E1] rounded text-[10px] font-mono flex items-center justify-center font-bold">
                                {m.code}
                              </span>
                              <span>{m.country}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 font-bold text-[#0F172A]">{m.primaryCurrency}</td>
                          <td className="py-4 px-6 font-mono text-[11px] text-[#475569]">
                            {m.supportedLanguages.join(', ')}
                          </td>
                          <td className="py-4 px-6 text-[#475569]">{m.settlementCutoff}</td>
                          <td className="py-4 px-6 text-[#64748B]">{m.regulatoryJurisdiction}</td>
                          <td className="py-4 px-6 text-right">
                            <span
                              className={`px-2.5 py-0.5 rounded text-[11px] font-semibold ${
                                m.status === 'Active'
                                  ? 'bg-[#DCFCE7] text-[#15803D]'
                                  : 'bg-[#EFF6FF] text-[#2563EB]'
                              }`}
                            >
                              {m.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* SUB-VIEW 4: INTEGRATIONS                                     */}
            {/* ============================================================= */}
            {activeSubTab === 'Integrations' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-bold text-[#0F172A]">
                    Enterprise Data & Platform Integrations
                  </h3>
                  <p className="text-xs text-[#64748B] mt-0.5">
                    Synchronize real-time transactional feeds with corporate ERPs, CRM systems, and SIEM security pipelines.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {integrations.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs space-y-4 flex flex-col justify-between"
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="text-[10px] font-bold text-[#0F4F4F] uppercase tracking-wider block">
                              {item.category}
                            </span>
                            <h4 className="text-sm font-bold text-[#0F172A] mt-0.5">
                              {item.name}
                            </h4>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                              item.status === 'Connected'
                                ? 'bg-[#DCFCE7] text-[#15803D]'
                                : 'bg-[#F1F5F9] text-[#475569]'
                            }`}
                          >
                            {item.status}
                          </span>
                        </div>
                        <p className="text-xs text-[#64748B] leading-relaxed">
                          {item.description}
                        </p>
                      </div>

                      <div className="pt-3 border-t border-[#F1F5F9] flex items-center justify-between text-xs text-[#64748B]">
                        <span className="font-mono text-[11px]">Sync: {item.syncInterval}</span>
                        <button
                          onClick={() => triggerToast(`Triggered instant re-sync for ${item.name}`)}
                          className="font-bold text-[#0F172A] hover:text-[#0F4F4F] cursor-pointer"
                        >
                          Trigger Sync
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* SUB-VIEW 5: FEATURE FLAGS                                    */}
            {/* ============================================================= */}
            {activeSubTab === 'Feature Flags' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-[#0F172A]">
                      Runtime Feature Flags & Experimentation
                    </h3>
                    <p className="text-xs text-[#64748B] mt-0.5">
                      Dynamically activate new capabilities, beta modules, or emergency killswitches across clusters.
                    </p>
                  </div>
                  <button
                    onClick={() => triggerToast('New flag creation wizard requires CPO dual-authorization.')}
                    className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl text-xs font-bold shadow-xs transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Create Flag</span>
                  </button>
                </div>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[11px]">
                        <th className="py-3.5 px-6">Flag & Description</th>
                        <th className="py-3.5 px-6">Target Cohort</th>
                        <th className="py-3.5 px-6">Impact</th>
                        <th className="py-3.5 px-6">Modified By</th>
                        <th className="py-3.5 px-6 text-right">State</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F1F5F9]">
                      {featureFlags.map((flag) => (
                        <tr key={flag.id} className="hover:bg-[#F8FAFC] transition-colors">
                          <td className="py-4 px-6">
                            <div className="font-bold text-[#0F172A]">{flag.name}</div>
                            <div className="font-mono text-[10px] text-[#0F4F4F] mt-0.5">{flag.key}</div>
                            <div className="text-[11px] text-[#64748B] mt-1">{flag.description}</div>
                          </td>
                          <td className="py-4 px-6 text-[#475569] font-medium">{flag.targetAudience}</td>
                          <td className="py-4 px-6">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                flag.impactLevel === 'Critical'
                                  ? 'bg-red-100 text-red-700'
                                  : flag.impactLevel === 'Medium'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {flag.impactLevel}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-[#64748B] text-[11px]">
                            <div>{flag.modifiedBy}</div>
                            <div className="text-[10px] text-[#94A3B8]">{flag.lastModified}</div>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                setFeatureFlags(
                                  featureFlags.map((f) =>
                                    f.id === flag.id ? { ...f, enabled: !f.enabled } : f
                                  )
                                );
                                triggerToast(`Toggled ${flag.name} to ${!flag.enabled ? 'ENABLED' : 'DISABLED'}`);
                              }}
                              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                flag.enabled ? 'bg-[#0F4F4F]' : 'bg-[#CBD5E1]'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                                  flag.enabled ? 'translate-x-4' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* SUB-VIEW 6: ENVIRONMENT VARIABLES & SECRETS (Never in Clear Text) */}
            {/* ============================================================= */}
            {activeSubTab === 'Environment' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-[#0F172A]">
                      Environment & Sealed Secret Store
                    </h3>
                    <p className="text-xs text-[#64748B] mt-0.5">
                      Production encryption keys, database strings, and TLS credentials. Strict Zero-Cleartext policy enforced.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" />
                      <span>HSM Envelope Sealed</span>
                    </span>
                  </div>
                </div>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[11px]">
                        <th className="py-3.5 px-6">Variable Key</th>
                        <th className="py-3.5 px-6">Masked Value</th>
                        <th className="py-3.5 px-6">Env</th>
                        <th className="py-3.5 px-6">Last Rotated</th>
                        <th className="py-3.5 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F1F5F9]">
                      {environmentSecrets.map((secret) => (
                        <tr key={secret.key} className="hover:bg-[#F8FAFC] transition-colors">
                          <td className="py-4 px-6 font-mono font-bold text-[#0F172A]">
                            <div className="flex items-center gap-2">
                              <Key className="w-3.5 h-3.5 text-[#64748B]" />
                              <span>{secret.key}</span>
                            </div>
                            <div className="text-[11px] font-sans font-normal text-[#64748B] mt-0.5">
                              {secret.description}
                            </div>
                          </td>
                          <td className="py-4 px-6 font-mono text-[11px] text-[#475569] max-w-xs break-all">
                            {secret.valueMasked}
                          </td>
                          <td className="py-4 px-6">
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded font-semibold text-[10px]">
                              {secret.environment}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-[#64748B] font-mono text-[11px]">
                            <div>{secret.lastRotated}</div>
                            <div className="text-[10px] text-[#94A3B8]">{secret.rotatedBy}</div>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <button
                              onClick={() => {
                                setSelectedSecretToRotate(secret);
                                setNewSecretValueInput('');
                                setSecretRotationReason('');
                              }}
                              className="px-3 py-1 bg-white border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#0F172A] font-bold rounded-lg text-xs cursor-pointer shadow-2xs"
                            >
                              Rotate Key
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* =============================================================== */}
      {/* 3. MODAL: CONFIGURE PROVIDER DRAWER                             */}
      {/* =============================================================== */}
      {selectedProviderToConfigure && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-end">
          <div className="bg-white w-full max-w-xl h-full shadow-2xl border-l border-[#E2E8F0] p-6 space-y-6 overflow-y-auto flex flex-col justify-between animate-in slide-in-from-right duration-300">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between pb-4 border-b border-[#F1F5F9]">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-[#F1F5F9] text-[#0F172A] rounded text-xs font-bold">
                      {selectedProviderToConfigure.serviceType}
                    </span>
                    <span className="px-2.5 py-0.5 bg-[#EFF6FF] text-[#2563EB] rounded text-xs font-semibold">
                      {selectedProviderToConfigure.environment}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mt-2">
                    Configure {selectedProviderToConfigure.name}
                  </h3>
                  <p className="text-xs text-[#64748B] mt-0.5">
                    {selectedProviderToConfigure.capability}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedProviderToConfigure(null)}
                  className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form Controls */}
              <div className="space-y-4 text-xs">
                {/* Status selector */}
                <div className="space-y-1.5">
                  <label className="font-bold text-[#0F172A] block">Operational Status</label>
                  <select
                    value={providerFormStatus}
                    onChange={(e: any) => setProviderFormStatus(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] outline-none"
                  >
                    <option value="Not configured">Not configured</option>
                    <option value="Testing">Testing</option>
                    <option value="Active">Active</option>
                    <option value="Disabled">Disabled</option>
                  </select>
                </div>

                {/* Target Environment */}
                <div className="space-y-1.5">
                  <label className="font-bold text-[#0F172A] block">Target Environment</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setProviderFormEnv('Sandbox')}
                      className={`p-3 rounded-xl border text-center font-bold cursor-pointer ${
                        providerFormEnv === 'Sandbox'
                          ? 'border-[#0F4F4F] bg-[#D1F2E7]/40 text-[#0F4F4F]'
                          : 'border-[#CBD5E1] bg-white text-[#64748B]'
                      }`}
                    >
                      Sandbox
                    </button>
                    <button
                      type="button"
                      onClick={() => setProviderFormEnv('Production')}
                      className={`p-3 rounded-xl border text-center font-bold cursor-pointer ${
                        providerFormEnv === 'Production'
                          ? 'border-[#0F4F4F] bg-[#D1F2E7]/40 text-[#0F4F4F]'
                          : 'border-[#CBD5E1] bg-white text-[#64748B]'
                      }`}
                    >
                      Production
                    </button>
                  </div>
                </div>

                {/* API Key (Encrypted Secret) */}
                <div className="space-y-1.5">
                  <label className="font-bold text-[#0F172A] flex items-center justify-between">
                    <span>API / Secret Key (Sealed Vault)</span>
                    <button
                      type="button"
                      onClick={() => setShowSecretKey(!showSecretKey)}
                      className="text-[#0F4F4F] hover:underline flex items-center gap-1 font-normal text-[11px]"
                    >
                      {showSecretKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      <span>{showSecretKey ? 'Mask Secret' : 'Reveal for 5s'}</span>
                    </button>
                  </label>
                  <input
                    type={showSecretKey ? 'text' : 'password'}
                    value={providerFormApiKey}
                    onChange={(e) => setProviderFormApiKey(e.target.value)}
                    placeholder="Enter new credential..."
                    className="w-full px-3 py-2 font-mono bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] outline-none"
                  />
                  <span className="text-[10px] text-[#64748B] block">
                    Credentials are saved into Hardware Security Module and encrypted at rest with AES-256-GCM.
                  </span>
                </div>

                {/* Webhook Endpoint */}
                <div className="space-y-1.5">
                  <label className="font-bold text-[#0F172A] block">Inbound Webhook Callback URL</label>
                  <input
                    type="text"
                    value={providerFormWebhook}
                    onChange={(e) => setProviderFormWebhook(e.target.value)}
                    className="w-full px-3 py-2 font-mono bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-[#F1F5F9]">
              <button
                type="button"
                onClick={() => {
                  triggerToast(`Triggered ping check for ${selectedProviderToConfigure.name}: 200 OK (118ms)`);
                }}
                className="px-3.5 py-2 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl text-xs cursor-pointer"
              >
                Test Connectivity
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedProviderToConfigure(null)}
                  className="px-4 py-2 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProviders(
                      providers.map((p) =>
                        p.id === selectedProviderToConfigure.id
                          ? {
                              ...p,
                              status: providerFormStatus,
                              environment: providerFormEnv,
                              apiKeyMasked: providerFormApiKey,
                              webhookEndpoint: providerFormWebhook,
                              configurationState:
                                providerFormStatus === 'Active'
                                  ? 'Verified'
                                  : providerFormStatus === 'Testing'
                                  ? 'Configured'
                                  : 'Draft',
                              runtimeStatus:
                                providerFormStatus === 'Active'
                                  ? 'Operational'
                                  : providerFormStatus === 'Testing'
                                  ? 'Testing'
                                  : 'Idle'
                            }
                          : p
                      )
                    );
                    setSelectedProviderToConfigure(null);
                    triggerToast(`Updated configuration for ${selectedProviderToConfigure.name}.`);
                  }}
                  className="px-5 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl text-xs cursor-pointer shadow-xs"
                >
                  Save Provider
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 4. MODAL: REGISTER CUSTOM PROVIDER                             */}
      {/* =============================================================== */}
      {showNewProviderModal && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
              <h3 className="text-base font-bold text-[#0F172A]">
                Register Custom Provider
              </h3>
              <button onClick={() => setShowNewProviderModal(false)} className="text-[#94A3B8] hover:text-[#0F172A]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div>
                <label className="font-bold text-[#0F172A] block mb-1">Provider Name</label>
                <input
                  type="text"
                  placeholder="e.g. Worldline, Adyen, Telesign..."
                  value={newCustomProviderName}
                  onChange={(e) => setNewCustomProviderName(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-[#0F172A] block mb-1">Service Type</label>
                <input
                  type="text"
                  placeholder="e.g. SEPA Direct Debit Clearing, KYC AI, HSM Vault..."
                  value={newCustomProviderService}
                  onChange={(e) => setNewCustomProviderService(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-[#0F172A] block mb-1">Capability Description</label>
                <textarea
                  rows={2}
                  placeholder="Describe technical capability & settlement rail..."
                  value={newCustomProviderCapability}
                  onChange={(e) => setNewCustomProviderCapability(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] outline-none resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => setShowNewProviderModal(false)}
                className="px-4 py-2 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!newCustomProviderName.trim()) return;
                  const newId = `prov-00${providers.length + 1}`;
                  const created: ProviderItem = {
                    id: newId,
                    name: newCustomProviderName,
                    serviceType: newCustomProviderService || 'Custom Rail',
                    status: 'Not configured',
                    capability: newCustomProviderCapability || 'Custom Infrastructure Service',
                    configurationState: 'Unset',
                    runtimeStatus: 'Idle',
                    lastCheck: 'Just registered',
                    environment: 'Sandbox',
                    apiKeyMasked: 'unset_••••••••••••••••••••'
                  };
                  setProviders([...providers, created]);
                  setShowNewProviderModal(false);
                  setNewCustomProviderName('');
                  setNewCustomProviderService('');
                  setNewCustomProviderCapability('');
                  triggerToast(`Registered new provider ${created.name}`);
                }}
                className="px-5 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl text-xs cursor-pointer shadow-xs"
              >
                Register Provider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 5. MODAL: MAINTENANCE MODE HIGH-RISK IMPACT & CONFIRMATION      */}
      {/* =============================================================== */}
      {showMaintenanceConfirmModal && (
        <div className="fixed inset-0 bg-[#0F172A]/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl space-y-5 animate-in zoom-in-95 duration-200 border border-red-200">
            {/* Header with High-Risk Warning */}
            <div className="flex items-start gap-3.5 pb-4 border-b border-[#F1F5F9]">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold uppercase">
                    High-Risk Action
                  </span>
                  <span className="text-xs font-mono text-[#64748B]">RBAC Tier 1 (Admin Only)</span>
                </div>
                <h3 className="text-base font-bold text-[#0F172A] mt-1">
                  {pendingMaintenanceState ? 'Activate Maintenance Mode?' : 'Deactivate Maintenance Mode?'}
                </h3>
              </div>
              <button
                onClick={() => setShowMaintenanceConfirmModal(false)}
                className="text-[#94A3B8] hover:text-[#0F172A]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Impact Preview */}
            <div className="p-4 bg-red-50/70 border border-red-200 rounded-xl space-y-2.5 text-xs text-red-900">
              <span className="font-bold block flex items-center gap-1.5 text-red-950">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span>System Impact Assessment</span>
              </span>
              <ul className="list-disc list-inside space-y-1 text-[11px] text-red-900/90 leading-relaxed">
                <li>
                  {pendingMaintenanceState
                    ? 'All public web ingress and merchant checkout sessions will return HTTP 503 Service Unavailable.'
                    : 'Public web ingress will resume immediately; merchants can submit new charges and refunds.'}
                </li>
                <li>
                  Active authenticated staff sessions (Control Center, Super Admin) will remain fully operational.
                </li>
                <li>
                  Scheduled background recon jobs and async ledger journal batching will continue in the background.
                </li>
              </ul>
            </div>

            {/* Justification input */}
            <div className="space-y-3 text-xs">
              <div className="space-y-1.5">
                <label className="font-bold text-[#0F172A] block">
                  Mandatory Change Justification & Ticket ID
                </label>
                <input
                  type="text"
                  placeholder="e.g. INFRA-4482: Cluster migration and database schema v2.4 upgrade."
                  value={maintenanceReason}
                  onChange={(e) => setMaintenanceReason(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-red-600 outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-[#0F172A] block">Estimated Downtime Window</label>
                <select
                  value={maintenanceEstimatedDuration}
                  onChange={(e) => setMaintenanceEstimatedDuration(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] outline-none"
                >
                  <option value="15 minutes">15 minutes</option>
                  <option value="30 minutes">30 minutes</option>
                  <option value="1 hour">1 hour</option>
                  <option value="2 hours">2 hours</option>
                  <option value="Indefinite / Critical Hotfix">Indefinite / Critical Hotfix</option>
                </select>
              </div>

              {/* Checkbox Acknowledgment */}
              <label className="flex items-start gap-2 pt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={maintenanceImpactAccepted}
                  onChange={(e) => setMaintenanceImpactAccepted(e.target.checked)}
                  className="mt-0.5 rounded border-[#CBD5E1] text-[#0F4F4F] focus:ring-[#0F4F4F]"
                />
                <span className="text-[11px] text-[#475569] leading-tight">
                  I understand this triggers an irrevocable high-priority entry in the immutable audit log and dispatches notifications to on-call engineering.
                </span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => setShowMaintenanceConfirmModal(false)}
                className="px-4 py-2 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                disabled={!maintenanceImpactAccepted || !maintenanceReason.trim()}
                onClick={handleConfirmMaintenanceMode}
                className={`px-5 py-2 rounded-xl text-xs font-bold text-white shadow-xs transition-all cursor-pointer ${
                  maintenanceImpactAccepted && maintenanceReason.trim()
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-red-300 opacity-50 cursor-not-allowed'
                }`}
              >
                Confirm {pendingMaintenanceState ? 'Activation' : 'Deactivation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 6. MODAL: REVIEW CHANGES (Dual Review / Save Approval)         */}
      {/* =============================================================== */}
      {showReviewChangesModal && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between pb-3 border-b border-[#F1F5F9]">
              <div>
                <h3 className="text-base font-bold text-[#0F172A]">
                  Review & Apply Configuration Changes
                </h3>
                <p className="text-xs text-[#64748B] mt-0.5">
                  The following parameters will be synced to cluster control nodes:
                </p>
              </div>
              <button onClick={() => setShowReviewChangesModal(false)} className="text-[#94A3B8] hover:text-[#0F172A]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 bg-[#F8FAFC] p-4 rounded-xl border border-[#E2E8F0] text-xs">
              <div className="flex justify-between items-center py-1 border-b border-[#E2E8F0]/60">
                <span className="text-[#64748B]">Platform Name:</span>
                <span className="font-bold text-[#0F172A]">{generalSettings.platformName}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-[#E2E8F0]/60">
                <span className="text-[#64748B]">Support Email:</span>
                <span className="font-mono font-bold text-[#0F172A]">{generalSettings.supportEmail}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-[#E2E8F0]/60">
                <span className="text-[#64748B]">Maintenance Mode:</span>
                <span
                  className={`font-bold ${
                    generalSettings.maintenanceMode ? 'text-red-600' : 'text-emerald-700'
                  }`}
                >
                  {generalSettings.maintenanceMode ? 'ENABLED (503)' : 'DISABLED (Normal)'}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-[#64748B]">Config Hash:</span>
                <span className="font-mono text-[10px] text-[#0F4F4F]">
                  SHA256: 7f8a9...b412
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => setShowReviewChangesModal(false)}
                className="px-4 py-2 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl text-xs cursor-pointer"
              >
                Back to Edit
              </button>
              <button
                onClick={handleSaveConfigurationSubmit}
                className="px-5 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl text-xs cursor-pointer shadow-xs flex items-center gap-1.5"
              >
                <Check className="w-3.5 h-3.5" />
                <span>Approve & Save</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 7. MODAL: ROTATE SECRET KEY                                    */}
      {/* =============================================================== */}
      {selectedSecretToRotate && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between pb-3 border-b border-[#F1F5F9]">
              <div>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-900 rounded text-[10px] font-bold uppercase">
                  Zero-Downtime Secret Rotation
                </span>
                <h3 className="text-base font-bold text-[#0F172A] mt-1">
                  Rotate {selectedSecretToRotate.key}
                </h3>
              </div>
              <button onClick={() => setSelectedSecretToRotate(null)} className="text-[#94A3B8] hover:text-[#0F172A]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-[11px] leading-relaxed">
                Rotation applies dual-key acceptance for 60 minutes across all nodes to ensure in-flight transactions are not terminated during TLS cache invalidation.
              </div>

              <div>
                <label className="font-bold text-[#0F172A] block mb-1">New Secret / Private Key Payload</label>
                <textarea
                  rows={3}
                  value={newSecretValueInput}
                  onChange={(e) => setNewSecretValueInput(e.target.value)}
                  placeholder="Paste new sealed credential or KMS key ARN..."
                  className="w-full px-3.5 py-2 font-mono bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] outline-none resize-none"
                />
              </div>

              <div>
                <label className="font-bold text-[#0F172A] block mb-1">Rotation Reason & Sign-off</label>
                <input
                  type="text"
                  placeholder="e.g. Periodic 90-day cryptographic key rotation policy"
                  value={secretRotationReason}
                  onChange={(e) => setSecretRotationReason(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:border-[#0F4F4F] outline-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => setSelectedSecretToRotate(null)}
                className="px-4 py-2 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                disabled={!newSecretValueInput.trim() || !secretRotationReason.trim()}
                onClick={() => {
                  setEnvironmentSecrets(
                    environmentSecrets.map((s) =>
                      s.key === selectedSecretToRotate.key
                        ? {
                            ...s,
                            lastRotated: '2026-08-28 (Just now)',
                            rotatedBy: 'Security Auditor (You)'
                          }
                        : s
                    )
                  );
                  setSelectedSecretToRotate(null);
                  triggerToast(`Successfully rotated ${selectedSecretToRotate.key}. Audit record created.`);
                }}
                className={`px-5 py-2 rounded-xl text-xs font-bold text-white shadow-xs transition-all cursor-pointer ${
                  newSecretValueInput.trim() && secretRotationReason.trim()
                    ? 'bg-[#0F4F4F] hover:bg-[#0A3D3D]'
                    : 'bg-gray-300 opacity-50 cursor-not-allowed'
                }`}
              >
                Execute Rotation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
